import Button from 'components/common/Button';
import { FadeIn } from 'components/common/FadeIn';
import { MainDrawDay } from 'constants/Draw';
import { useEnterStore } from 'hooks/stores/useEnterStore';
import { useIsMobile } from 'hooks/ui/useIsMobile';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { formattedPriceStringFromPence } from 'utils/common';

import Envelope from '../../svg/Envelope.svg';
import PlusRing from '../../svg/PlusRing.svg';
import TealRadio from '../../svg/TealRadio.svg';
import DrawDaysSelector from '../DrawDaysSelector';
import HeadingAndSubheading from '../HeadingAndSubheading';
import MembershipSummary from '../MembershipSummary';
import { MainEnterFlowNavigation, V2NavigationRight } from '../Navigation';
import EntryWrapper, { EntryBreaker, EntryContent } from '../OtherEntry';
import PostalEntrySlidingModal from '../PostalEntrySlidingModal';
import TitleSection, { InfoGraphicRow } from '../TitleSection';
import V2EnterLayout from './V2EnterLayout';
import styles from './V2EntriesAndDays.module.css';
import V2EntriesPicker from './V2EntriesPicker';

interface V2EntriesAndDaysProps {
  nextDrawDay: MainDrawDay;
}
interface EntryLineCountPickerProps {
  title?: string | React.ReactNode;
  subtitle?: string;
}

const EntryLineCountPicker = observer(
  ({ title, subtitle }: EntryLineCountPickerProps) => {
    const enterStore = useEnterStore();
    const { isMobile } = useIsMobile();
    const [postalModalOpen, setPostalModalOpen] = useState(false);

    const allEntryCounts = Object.entries(
      enterStore.plan?.priceTable[0].GBP || [],
    );
    const singleEntryCost = allEntryCounts[0] ? allEntryCounts[0][1] : 0;

    const selectEntries = (entryNum: number) => {
      const nextEntryNum = entryNum === enterStore.lines.length ? 1 : entryNum;
      enterStore.setNumEntries(nextEntryNum);
    };

    return (
      <div>
        <TitleSection
          title={title}
          subtitle={subtitle}
          displayMascot={!isMobile}
        />
        <div className={styles.headingContainer}>
          <HeadingAndSubheading
            heading="More entries, more chances"
            subheading="Want more chances to win?"
          />
        </div>
        <div className={styles.entriesContainer}>
          {allEntryCounts.map(([min_number_entries, price_per_entry]) => {
            if (min_number_entries === '1') return null;

            const minNumEntriesInt = parseInt(min_number_entries);
            const selected = minNumEntriesInt === enterStore.lines.length;

            return (
              <FadeIn
                onScroll
                duration={0.3}
                delay={(minNumEntriesInt - 1) * 0.05}
                key={min_number_entries}
              >
                <EntryWrapper
                  onClick={() => selectEntries(minNumEntriesInt)}
                  badge={min_number_entries === '2' ? 'POPULAR' : undefined}
                  badgeColor="teal"
                  selected={selected}
                >
                  <EntryContent
                    icon={<PlusRing />}
                    iconOnSelected={<TealRadio />}
                    textTitle={`Entry ${min_number_entries}`}
                    textOrange={`Save ${singleEntryCost - price_per_entry}p`}
                    rightContent={
                      <div className="daymadeTypographySRegular textLightGrey">
                        {formattedPriceStringFromPence(price_per_entry)} / entry
                      </div>
                    }
                    selected={selected}
                  />
                </EntryWrapper>
              </FadeIn>
            );
          })}
          <FadeIn onScroll duration={0.3} delay={0.1}>
            <EntryBreaker />
          </FadeIn>
          <FadeIn onScroll duration={0.3} delay={0.15}>
            <EntryWrapper onClick={() => setPostalModalOpen(true)}>
              <EntryContent
                icon={<Envelope />}
                textTitle="Postal entry"
                textSubtitle="No purchase necessary"
                rightContent={
                  <Button
                    buttonStyle="Tertiary"
                    buttonSize="Small"
                    type={undefined}
                  >
                    Read More
                  </Button>
                }
              />
            </EntryWrapper>
          </FadeIn>
        </div>
        <div className={styles.modalsContainer}>
          <PostalEntrySlidingModal
            isOpen={postalModalOpen}
            onClose={() => setPostalModalOpen(false)}
          />
        </div>
      </div>
    );
  },
);

export const enterFlowTitle = (checkoutPage?: boolean) => (
  <>
    {checkoutPage ? (
      <>
        You're almost <br />
        there...
      </>
    ) : (
      <>
        Build your <br />
        membership
      </>
    )}
  </>
);

export const enterFlowSubtitle = (checkoutPage?: boolean) =>
  checkoutPage
    ? 'Just to confirm a couple of details'
    : 'The more numbers you match, the bigger the prize';

export const V2EntriesAndDays: React.FC<V2EntriesAndDaysProps> = ({
  nextDrawDay,
}) => {
  const title = enterFlowTitle();
  const subtitle = enterFlowSubtitle();

  return (
    <V2EnterLayout
      desktopLeft={
        <>
          <MainEnterFlowNavigation />
          <EntryLineCountPicker title={title} subtitle={subtitle} />
          <DrawDaysSelector nextDrawDay={nextDrawDay} />
        </>
      }
      desktopRight={
        <>
          <V2NavigationRight />
          <FadeIn onScroll>
            <InfoGraphicRow desktopVersion />
          </FadeIn>
          <V2EntriesPicker />
          <MembershipSummary />
        </>
      }
      mobileBottom={
        <>
          <EntryLineCountPicker />
          <DrawDaysSelector nextDrawDay={nextDrawDay} />
          <MembershipSummary onLightBackground />
        </>
      }
    />
  );
};

export default V2EntriesAndDays;
